import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  InputAdornment,
  IconButton,
  Stack,
  Alert,
  Snackbar,
  FormHelperText,
  Divider,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Kbd,
  alpha
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  ProductionQuantityLimits as ProductIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Straighten as UnitIcon,
  Receipt as TaxIcon,
  LocalShipping as SupplierIcon,
  QrCode as BarcodeIcon,
  Inventory as InventoryIcon,
  Inventory2 as QuantityIcon,
  Image as ImageIcon,
  Add as AddIcon,
  KeyboardReturn as EnterIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import categoryService from '../../services/categoryService';
import unitService from '../../services/unitService';
import taxService from '../../services/taxService';
import { supplierService } from '../../services/supplierService';
import { getCurrentUser } from '../../services/authService';
import productService from '../../services/productService';
import { toast } from 'react-hot-toast';

const logDebug = (message, data) => {
  console.log(`[ProductForm Debug] ${message}:`, data);
};

const ProductFormEnhanced = ({
  initialData = null,
  onSubmit,
  onCancel,
  isViewMode = false,
  // Accept pre-fetched data as props to avoid redundant API calls
  categoriesProp = null,
  unitsProp = null,
  taxesProp = null
}) => {
  const user = getCurrentUser();
  const companyId = user?.companyId;

  // Refs for keyboard navigation
  const firstInputRef = useRef(null);
  const submitButtonRef = useRef(null);

  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    productCode: '',
    categoryId: '',
    unitId: '',
    taxId: '',
    hsnCode: '',
    purchasePrice: '',
    mrp: '',
    sellingPrice: '', // Retailer rate
    withTax: '', // Retailer rate with tax (UI only)
    wholesalePrice: '', // Wholesale rate
    wholesaleWithTax: '', // Wholesale rate with tax (UI only)
    minSalePrice: '',
    quantity: '0',
    reorderLevel: '',
    imageFile: null,
    imagePreview: '',
    imageUrl: '',
    status: 'ACTIVE',
    manageable: true,
    // Sub Unit 1 fields
    subUnit1Id: '',
    subUnit1ConversionRatio: '',
    subUnit1SellingPrice: '',
    // Sub Unit 2 fields
    subUnit2Id: '',
    subUnit2ConversionRatio: '',
    subUnit2SellingPrice: '',
    supplierId: ''
  });

  // Track which field was last edited to prevent circular updates
  const [lastEditedPriceField, setLastEditedPriceField] = useState(null);

  // Entity lists - initialize with props if provided
  const [categories, setCategories] = useState(categoriesProp || []);
  const [units, setUnits] = useState(unitsProp || []);
  const [taxes, setTaxes] = useState(taxesProp || []);
  const [suppliers, setSuppliers] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingTaxes, setLoadingTaxes] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Error and validation
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [imageError, setImageError] = useState('');

  // Quick create dialogs
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [taxDialogOpen, setTaxDialogOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  // Quick create form data
  const [newCategory, setNewCategory] = useState({ name: '' });
  const [newUnit, setNewUnit] = useState({ name: '', abbreviation: '' });
  const [newTax, setNewTax] = useState({ name: '', rate: '' });

  // Auto-complete selected values
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedTax, setSelectedTax] = useState(null);
  const [selectedSubUnit1, setSelectedSubUnit1] = useState(null);
  const [selectedSubUnit2, setSelectedSubUnit2] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ctrl/Cmd + S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!isViewMode && !loading) {
          submitButtonRef.current?.click();
        }
      }
      // Escape to cancel
      if (e.key === 'Escape' && !categoryDialogOpen && !unitDialogOpen && !taxDialogOpen) {
        onCancel();
      }
      // Ctrl + H for help
      if ((e.ctrlKey || e.metaKey) && e.key === 'h') {
        e.preventDefault();
        setHelpDialogOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onCancel, isViewMode, loading, categoryDialogOpen, unitDialogOpen, taxDialogOpen]);

  // Focus first input on mount
  useEffect(() => {
    if (!isViewMode && firstInputRef.current) {
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
  }, [isViewMode]);

  // Update state if props change
  useEffect(() => {
    if (categoriesProp && categoriesProp.length > 0) {
      console.log("ProductFormEnhanced: Using categories from props");
      setCategories(categoriesProp);
    }
  }, [categoriesProp]);

  useEffect(() => {
    if (unitsProp && unitsProp.length > 0) {
      console.log("ProductFormEnhanced: Using units from props");
      setUnits(unitsProp);
    }
  }, [unitsProp]);

  useEffect(() => {
    if (taxesProp && taxesProp.length > 0) {
      console.log("ProductFormEnhanced: Using taxes from props");
      setTaxes(taxesProp);
    }
  }, [taxesProp]);

  // Fetch data ONLY if not provided as props
  useEffect(() => {
    let isMounted = true;

    const fetchCategories = async () => {
      // Only fetch if not provided as props
      if (categoriesProp || categories.length > 0) {
        console.log("ProductFormEnhanced: Categories already available, skipping fetch");
        return;
      }

      try {
        setLoadingCategories(true);
        const response = await categoryService.getAllCategories();
        if (isMounted && response?.content) {
          console.log("ProductFormEnhanced: Fetched categories from API");
          setCategories(response.content);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      } finally {
        if (isMounted) setLoadingCategories(false);
      }
    };

    const fetchUnits = async () => {
      // Only fetch if not provided as props
      if (unitsProp || units.length > 0) {
        console.log("ProductFormEnhanced: Units already available, skipping fetch");
        return;
      }

      try {
        setLoadingUnits(true);
        const response = await unitService.getAllUnits();
        if (isMounted && response?.content) {
          console.log("ProductFormEnhanced: Fetched units from API");
          setUnits(response.content);
        }
      } catch (err) {
        console.error("Error fetching units:", err);
      } finally {
        if (isMounted) setLoadingUnits(false);
      }
    };

    const fetchTaxes = async () => {
      // Only fetch if not provided as props
      if (taxesProp || taxes.length > 0) {
        console.log("ProductFormEnhanced: Taxes already available, skipping fetch");
        return;
      }

      try {
        setLoadingTaxes(true);
        const data = await productService.getTaxes();
        if (isMounted && Array.isArray(data)) {
          console.log("ProductFormEnhanced: Fetched taxes from API");
          setTaxes(data);
        }
      } catch (err) {
        console.error('Error fetching taxes:', err);
      } finally {
        if (isMounted) setLoadingTaxes(false);
      }
    };

    const fetchSuppliers = async () => {
      try {
        setLoadingSuppliers(true);
        let list = [];
        if (companyId) {
          try {
            list = await supplierService.getSuppliersByCompany(companyId);
          } catch {
            list = [];
          }
        }
        if (list.length === 0) {
          const response = await supplierService.getAllSuppliers();
          list = response?.content ?? (Array.isArray(response) ? response : []);
        }
        if (isMounted && Array.isArray(list)) {
          setSuppliers(list);
        }
      } catch (err) {
        console.error('Error fetching suppliers:', err);
      } finally {
        if (isMounted) setLoadingSuppliers(false);
      }
    };

    fetchCategories();
    fetchUnits();
    fetchTaxes();
    fetchSuppliers();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Initialize form with existing data
  useEffect(() => {
    if (categories.length > 0 && units.length > 0) {
      if (initialData) {
        try {
          let categoryId = '';
          if (initialData.categoryId) categoryId = String(initialData.categoryId);
          else if (initialData.category?.id) categoryId = String(initialData.category.id);

          let unitId = '';
          if (initialData.unitId) unitId = String(initialData.unitId);
          else if (initialData.unit?.id) unitId = String(initialData.unit.id);

          let taxId = '';
          if (initialData.taxId) taxId = String(initialData.taxId);
          else if (initialData.tax?.id) taxId = String(initialData.tax.id);

          let subUnit1Id = '';
          if (initialData.subUnit1Id) subUnit1Id = String(initialData.subUnit1Id);
          else if (initialData.subUnit1?.id) subUnit1Id = String(initialData.subUnit1.id);

          let subUnit2Id = '';
          if (initialData.subUnit2Id) subUnit2Id = String(initialData.subUnit2Id);
          else if (initialData.subUnit2?.id) subUnit2Id = String(initialData.subUnit2.id);

          let supplierId = '';
          if (initialData.supplierId) supplierId = String(initialData.supplierId);
          else if (initialData.supplier?.id) supplierId = String(initialData.supplier.id);

          // Calculate withTax based on sellingPrice and tax rate (retailer)
          const sellingPrice = parseFloat(initialData.sellingPrice) || 0;
          let withTaxValue = '';
          if (taxId) {
            const tax = taxes.find(t => String(t.id) === taxId);
            if (tax && tax.rate) {
              withTaxValue = (sellingPrice * (1 + tax.rate / 100)).toFixed(2);
            } else {
              withTaxValue = sellingPrice.toString();
            }
          } else {
            withTaxValue = sellingPrice.toString();
          }

          // Wholesale: price and with-tax
          const wholesalePrice = parseFloat(initialData.wholesalePrice) || 0;
          let wholesaleWithTaxValue = '';
          if (taxId && wholesalePrice > 0) {
            const tax = taxes.find(t => String(t.id) === taxId);
            if (tax && tax.rate) {
              wholesaleWithTaxValue = (wholesalePrice * (1 + tax.rate / 100)).toFixed(2);
            } else {
              wholesaleWithTaxValue = wholesalePrice.toString();
            }
          } else if (wholesalePrice > 0) {
            wholesaleWithTaxValue = wholesalePrice.toString();
          }

          setFormData({
            name: initialData.name || '',
            sku: initialData.sku || '',
            productCode: initialData.productCode || '',
            categoryId,
            unitId,
            taxId,
            hsnCode: initialData.hsnCode || '',
            purchasePrice: initialData.purchasePrice || '',
            mrp: initialData.mrp || '',
            sellingPrice: initialData.sellingPrice || '',
            withTax: withTaxValue,
            wholesalePrice: initialData.wholesalePrice != null && initialData.wholesalePrice !== '' ? String(initialData.wholesalePrice) : '',
            wholesaleWithTax: wholesaleWithTaxValue,
            minSalePrice: initialData.minSalePrice || '',
            quantity: initialData.quantity || '0',
            reorderLevel: initialData.reorderLevel || '',
            imageFile: null,
            imagePreview: '',
            imageUrl: initialData.imageUrl || '',
            status: initialData.status || 'ACTIVE',
            manageable: typeof initialData.manageable === 'boolean' ? initialData.manageable : true,
            // Sub Unit 1
            subUnit1Id,
            subUnit1ConversionRatio: initialData.subUnit1ConversionRatio || '',
            subUnit1SellingPrice: initialData.subUnit1SellingPrice || '',
            // Sub Unit 2
            subUnit2Id,
            subUnit2ConversionRatio: initialData.subUnit2ConversionRatio || '',
            subUnit2SellingPrice: initialData.subUnit2SellingPrice || '',
            supplierId
          });

          // Set autocomplete selections
          if (categoryId) {
            const cat = categories.find(c => String(c.id) === categoryId);
            setSelectedCategory(cat || null);
          } else {
            setSelectedCategory(null);
          }

          if (unitId) {
            const unit = units.find(u => String(u.id) === unitId);
            setSelectedUnit(unit || null);
          } else {
            setSelectedUnit(null);
          }

          if (taxId) {
            const tax = taxes.find(t => String(t.id) === taxId);
            setSelectedTax(tax || null);
          }

          if (subUnit1Id) {
            const subUnit1 = units.find(u => String(u.id) === subUnit1Id);
            setSelectedSubUnit1(subUnit1 || null);
          }

          if (subUnit2Id) {
            const subUnit2 = units.find(u => String(u.id) === subUnit2Id);
            setSelectedSubUnit2(subUnit2 || null);
          }

          if (supplierId) {
            const supplier = suppliers.find(s => String(s.id) === supplierId);
            setSelectedSupplier(supplier || null);
          } else {
            setSelectedSupplier(null);
          }

          if (initialData.imageUrl) {
            setFormData(prev => ({
              ...prev,
              imagePreview: productService.getProductImageUrl(initialData.imageUrl)
            }));
          }

        } catch (err) {
          console.error('Error setting form data:', err);
        }
      } else {
        // Set default unit to PCS if available
        const pcsUnit = units.find(u => u.name?.toUpperCase() === 'PCS' || u.abbreviation?.toUpperCase() === 'PCS');
        if (pcsUnit) {
          setFormData(prev => ({
            ...prev,
            unitId: String(pcsUnit.id)
          }));
          setSelectedUnit(pcsUnit);
        }
      }
    }
  }, [initialData, categories, units, taxes, suppliers]);

  // Auto-calculate between sellingPrice and withTax based on tax rate
  useEffect(() => {
    const taxRate = selectedTax?.rate || 0;

    // Skip if neither field has a value
    if (!formData.sellingPrice && !formData.withTax) {
      return;
    }

    // If no tax, both fields should be the same
    if (!taxRate || taxRate === 0) {
      // Only sync when the edited field has a value; do not repopulate when clearing
      if (lastEditedPriceField === 'sellingPrice' && formData.sellingPrice) {
        setFormData(prev => ({ ...prev, withTax: formData.sellingPrice }));
      } else if (lastEditedPriceField === 'withTax' && formData.withTax) {
        setFormData(prev => ({ ...prev, sellingPrice: formData.withTax }));
      }
      return;
    }

    // Calculate based on which field was last edited
    if (lastEditedPriceField === 'sellingPrice' && formData.sellingPrice) {
      const sellingPrice = parseFloat(formData.sellingPrice);
      if (!isNaN(sellingPrice) && sellingPrice >= 0) {
        const withTaxValue = sellingPrice * (1 + taxRate / 100);
        setFormData(prev => ({
          ...prev,
          withTax: withTaxValue.toFixed(2)
        }));
      }
    } else if (lastEditedPriceField === 'withTax' && formData.withTax) {
      const withTaxValue = parseFloat(formData.withTax);
      if (!isNaN(withTaxValue) && withTaxValue >= 0) {
        const sellingPriceValue = withTaxValue / (1 + taxRate / 100);
        setFormData(prev => ({
          ...prev,
          sellingPrice: sellingPriceValue.toFixed(2)
        }));
      }
    } else if (formData.sellingPrice && !lastEditedPriceField) {
      // When tax is first selected or changed, calculate withTax from sellingPrice
      const sellingPrice = parseFloat(formData.sellingPrice);
      if (!isNaN(sellingPrice) && sellingPrice >= 0) {
        const withTaxValue = sellingPrice * (1 + taxRate / 100);
        setFormData(prev => ({
          ...prev,
          withTax: withTaxValue.toFixed(2)
        }));
      }
    }
  }, [formData.sellingPrice, formData.withTax, selectedTax, lastEditedPriceField]);

  // Auto-calculate between wholesalePrice and wholesaleWithTax based on tax rate
  useEffect(() => {
    const taxRate = selectedTax?.rate || 0;
    if (!formData.wholesalePrice && !formData.wholesaleWithTax) return;
    if (!taxRate || taxRate === 0) {
      if (lastEditedPriceField === 'wholesalePrice' && formData.wholesalePrice) {
        setFormData(prev => ({ ...prev, wholesaleWithTax: formData.wholesalePrice }));
      } else if (lastEditedPriceField === 'wholesaleWithTax' && formData.wholesaleWithTax) {
        setFormData(prev => ({ ...prev, wholesalePrice: formData.wholesaleWithTax }));
      }
      return;
    }
    if (lastEditedPriceField === 'wholesalePrice' && formData.wholesalePrice) {
      const wholesalePrice = parseFloat(formData.wholesalePrice);
      if (!isNaN(wholesalePrice) && wholesalePrice >= 0) {
        setFormData(prev => ({ ...prev, wholesaleWithTax: (wholesalePrice * (1 + taxRate / 100)).toFixed(2) }));
      }
    } else if (lastEditedPriceField === 'wholesaleWithTax' && formData.wholesaleWithTax) {
      const wholesaleWithTaxValue = parseFloat(formData.wholesaleWithTax);
      if (!isNaN(wholesaleWithTaxValue) && wholesaleWithTaxValue >= 0) {
        setFormData(prev => ({ ...prev, wholesalePrice: (wholesaleWithTaxValue / (1 + taxRate / 100)).toFixed(2) }));
      }
    } else if (formData.wholesalePrice && !lastEditedPriceField) {
      const wholesalePrice = parseFloat(formData.wholesalePrice);
      if (!isNaN(wholesalePrice) && wholesalePrice >= 0) {
        setFormData(prev => ({ ...prev, wholesaleWithTax: (wholesalePrice * (1 + taxRate / 100)).toFixed(2) }));
      }
    }
  }, [formData.wholesalePrice, formData.wholesaleWithTax, selectedTax, lastEditedPriceField]);

  // Auto-calculate sub-unit prices when main price or conversion ratios change
  useEffect(() => {
    if (formData.sellingPrice && formData.subUnit1ConversionRatio) {
      const mainPrice = parseFloat(formData.sellingPrice);
      const ratio1 = parseFloat(formData.subUnit1ConversionRatio);

      if (mainPrice > 0 && ratio1 > 0) {
        const sub1Price = mainPrice / ratio1;
        setFormData(prev => ({
          ...prev,
          subUnit1SellingPrice: sub1Price.toFixed(2)
        }));

        // If Sub Unit 2 ratio exists, calculate Sub Unit 2 price
        if (formData.subUnit2ConversionRatio) {
          const ratio2 = parseFloat(formData.subUnit2ConversionRatio);
          if (ratio2 > 0) {
            const sub2Price = sub1Price / ratio2;
            setFormData(prev => ({
              ...prev,
              subUnit2SellingPrice: sub2Price.toFixed(2)
            }));
          }
        }
      }
    } else {
      // Clear prices if main price or ratio is missing
      if (!formData.sellingPrice || !formData.subUnit1ConversionRatio) {
        setFormData(prev => ({ ...prev, subUnit1SellingPrice: '' }));
      }
      if (!formData.subUnit1ConversionRatio || !formData.subUnit2ConversionRatio) {
        setFormData(prev => ({ ...prev, subUnit2SellingPrice: '' }));
      }
    }
  }, [formData.sellingPrice, formData.subUnit1ConversionRatio, formData.subUnit2ConversionRatio]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validation for text fields (max 25 characters)
    if (['name', 'sku', 'hsnCode', 'productCode'].includes(name)) {
      if (value.length > 50) return;
    }

    // Validation for number fields (max 6 digits)
    if (['purchasePrice', 'sellingPrice', 'wholesalePrice', 'mrp', 'quantity', 'reorderLevel', 'subUnit1SellingPrice', 'subUnit2SellingPrice'].includes(name)) {
      if (value.length > 6) return;
    }

    // Track which price field was edited for tax calculation
    if (['sellingPrice', 'withTax', 'wholesalePrice', 'wholesaleWithTax'].includes(name)) {
      setLastEditedPriceField(name);
    }

    // Allow empty value for number fields to enable clearing/backspace
    if (value === '') {
      if (name === 'sellingPrice') {
        setFormData(prev => ({ ...prev, sellingPrice: '', withTax: '' }));
      } else if (name === 'withTax') {
        setFormData(prev => ({ ...prev, withTax: '', sellingPrice: '' }));
      } else if (name === 'wholesalePrice') {
        setFormData(prev => ({ ...prev, wholesalePrice: '', wholesaleWithTax: '' }));
      } else if (name === 'wholesaleWithTax') {
        setFormData(prev => ({ ...prev, wholesaleWithTax: '', wholesalePrice: '' }));
      } else {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
      if (formErrors[name]) {
        setFormErrors({ ...formErrors, [name]: null });
      }
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageError('');

    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setImageError('Unsupported format. Please upload JPEG or PNG.');
      toast.error('Please upload a valid image file (JPEG or PNG)');
      return;
    }

    const minSize = 1 * 1024; // 1KB
    const maxSize = 500 * 1024; // 500KB

    if (file.size < minSize || file.size > maxSize) {
      if (file.size < minSize) {
        setImageError('Image is smaller than 1KB. Please upload 1KB–500KB.');
      } else {
        setImageError('Image exceeds 500KB. Please upload 1KB–500KB.');
      }
      toast.error('Image size should be between 1KB and 500KB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        imagePreview: reader.result
      }));
    };
    reader.readAsDataURL(file);

    try {
      setUploadLoading(true);
      const imageUrl = await productService.uploadProductImage(file);
      setFormData(prev => ({ ...prev, imageUrl }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      imageFile: null,
      imagePreview: '',
      imageUrl: ''
    }));
    setImageError('');
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) errors.name = 'Product name is required';
    if (!formData.categoryId) errors.categoryId = 'Category is required';
    if (!formData.unitId) errors.unitId = 'Unit is required';

    // MRP is optional, but if provided must be valid
    if (formData.mrp && parseFloat(formData.mrp) < 0) {
      errors.mrp = 'MRP cannot be negative';
    }

    // Purchase price is optional, but if provided must be valid
    if (formData.purchasePrice && parseFloat(formData.purchasePrice) < 0) {
      errors.purchasePrice = 'Purchase price cannot be negative';
    }

    // Retailer rate (selling price) is required
    if (!formData.sellingPrice || parseFloat(formData.sellingPrice) <= 0) {
      errors.sellingPrice = 'Valid retailer rate is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);

      // SKU: send as-is. If empty, backend will auto-generate from company settings (prefix + pattern)
      const sku = formData.sku?.trim() || '';

      // Remove UI-only fields (withTax, wholesaleWithTax) from submitData
      const { withTax, wholesaleWithTax, ...dataToSubmit } = formData;

      const submitData = {
        ...dataToSubmit,
        sku,
        companyId: parseInt(companyId) || 1,
        categoryId: parseInt(formData.categoryId),
        unitId: parseInt(formData.unitId),
        taxId: formData.taxId ? parseInt(formData.taxId) : null,
        supplierId: formData.supplierId ? parseInt(formData.supplierId) : null,
        hsnCode: formData.hsnCode?.trim() || null,
        purchasePrice: parseFloat(formData.purchasePrice) || 0,
        mrp: formData.mrp ? parseFloat(formData.mrp) : null,
        sellingPrice: parseFloat(formData.sellingPrice) || 0,
        wholesalePrice: formData.wholesalePrice ? parseFloat(formData.wholesalePrice) : null,
        minSalePrice: parseFloat(formData.minSalePrice) || 0,
        quantity: parseInt(formData.quantity || '0'),
        reorderLevel: formData.reorderLevel ? parseInt(formData.reorderLevel) : null,
        status: formData.status || 'ACTIVE',
        imageUrl: formData.imageUrl,
        manageable: !!formData.manageable,
        // Sub Unit 1
        subUnit1Id: formData.subUnit1Id ? parseInt(formData.subUnit1Id) : null,
        subUnit1ConversionRatio: formData.subUnit1ConversionRatio ? parseFloat(formData.subUnit1ConversionRatio) : null,
        subUnit1SellingPrice: formData.subUnit1SellingPrice ? parseFloat(formData.subUnit1SellingPrice) : null,
        // Sub Unit 2
        subUnit2Id: formData.subUnit2Id ? parseInt(formData.subUnit2Id) : null,
        subUnit2ConversionRatio: formData.subUnit2ConversionRatio ? parseFloat(formData.subUnit2ConversionRatio) : null,
        subUnit2SellingPrice: formData.subUnit2SellingPrice ? parseFloat(formData.subUnit2SellingPrice) : null
      };

      delete submitData.imageFile;
      delete submitData.imagePreview;

      await onSubmit(submitData);
      toast.success(initialData ? 'Product updated successfully' : 'Product created successfully');
    } catch (err) {
      console.error('Error submitting form:', err);
      toast.error(err.response?.data?.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  // Quick create handlers
  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      const created = await categoryService.createCategory({
        name: newCategory.name.trim(),
        companyId: parseInt(companyId)
      });

      setCategories(prev => [...prev, created]);
      setSelectedCategory(created);
      setFormData(prev => ({ ...prev, categoryId: String(created.id) }));
      setCategoryDialogOpen(false);
      setNewCategory({ name: '' });
      toast.success('Category created successfully');
    } catch (err) {
      toast.error('Failed to create category');
    }
  };

  const handleCreateUnit = async () => {
    if (!newUnit.name.trim() || !newUnit.abbreviation.trim()) {
      toast.error('Unit name and abbreviation are required');
      return;
    }

    try {
      const created = await unitService.createUnit({
        name: newUnit.name.trim(),
        abbreviation: newUnit.abbreviation.trim(),
        companyId: parseInt(companyId)
      });

      setUnits(prev => [...prev, created]);
      setSelectedUnit(created);
      setFormData(prev => ({ ...prev, unitId: String(created.id) }));
      setUnitDialogOpen(false);
      setNewUnit({ name: '', abbreviation: '' });
      toast.success('Unit created successfully');
    } catch (err) {
      toast.error('Failed to create unit');
    }
  };

  const handleCreateTax = async () => {
    if (!newTax.name.trim() || !newTax.rate) {
      toast.error('Tax name and rate are required');
      return;
    }

    try {
      const created = await taxService.createTax({
        name: newTax.name.trim(),
        rate: parseFloat(newTax.rate),
        companyId: parseInt(companyId)
      });

      setTaxes(prev => [...prev, created]);
      setSelectedTax(created);
      setFormData(prev => ({ ...prev, taxId: String(created.id) }));
      setTaxDialogOpen(false);
      setNewTax({ name: '', rate: '' });
      toast.success('Tax created successfully');
    } catch (err) {
      toast.error('Failed to create tax');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* All Fields in One Box */}
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Product Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              {/* Row 1: Product Name and SKU */}
              <Grid item xs={12} sm={6}>
                <TextField
                  inputRef={firstInputRef}
                  fullWidth
                  required
                  size="small"
                  label="Product Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ProductIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="SKU (Auto-generated if empty)"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BarcodeIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Row 2: Category and Tax */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  value={selectedCategory}
                  onChange={(event, newValue) => {
                    if (newValue === 'add-new') {
                      setCategoryDialogOpen(true);
                    } else {
                      setSelectedCategory(newValue);
                      setFormData(prev => ({
                        ...prev,
                        categoryId: newValue ? String(newValue.id) : ''
                      }));
                    }
                  }}
                  options={[...categories, 'add-new']}
                  getOptionLabel={(option) => {
                    if (option === 'add-new') return '+ Add New Category';
                    return option?.name || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (option === 'add-new') return false;
                    return option?.id === value?.id;
                  }}
                  loading={loadingCategories}
                  disabled={isViewMode || loading}
                  autoHighlight
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option === 'add-new' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                          <AddIcon sx={{ mr: 1 }} fontSize="small" />
                          Add New Category
                        </Box>
                      ) : (
                        option.name
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      required
                      size="small"
                      error={!!formErrors.categoryId}
                      helperText={formErrors.categoryId}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <CategoryIcon color="primary" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  value={selectedTax}
                  onChange={(event, newValue) => {
                    if (newValue === 'add-new') {
                      setTaxDialogOpen(true);
                    } else {
                      setSelectedTax(newValue);
                      setFormData(prev => ({
                        ...prev,
                        taxId: newValue ? String(newValue.id) : ''
                      }));
                    }
                  }}
                  options={[...taxes.filter(t => (t.status === 'ACTIVE' || !t.status) || (selectedTax && t.id === selectedTax.id)), 'add-new']}
                  getOptionLabel={(option) => {
                    if (option === 'add-new') return '+ Add New Tax';
                    return option?.name || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (option === 'add-new') return false;
                    return option?.id === value?.id;
                  }}
                  loading={loadingTaxes}
                  disabled={isViewMode || loading}
                  autoHighlight
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option === 'add-new' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                          <AddIcon sx={{ mr: 1 }} fontSize="small" />
                          Add New Tax
                        </Box>
                      ) : (
                        option.name
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tax (Optional)"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <TaxIcon color="primary" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Row 3: HSN Code */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="HSN Code"
                  name="hsnCode"
                  value={formData.hsnCode}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  error={!!formErrors.hsnCode}
                  helperText={formErrors.hsnCode}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BarcodeIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Quantity"
                  name="quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <QuantityIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  helperText=""
                />
              </Grid>

              {/* Row 4: MRP and Purchase Price */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="MRP"
                  name="mrp"
                  type="number"
                  value={formData.mrp}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  error={!!formErrors.mrp}
                  helperText={formErrors.mrp}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Purchase Price"
                  name="purchasePrice"
                  type="number"
                  value={formData.purchasePrice}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  error={!!formErrors.purchasePrice}
                  helperText={formErrors.purchasePrice}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>

              {/* Row 5: Retailer Rate and With Tax */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Retailer Rate"
                  name="sellingPrice"
                  type="number"
                  value={formData.sellingPrice}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  error={!!formErrors.sellingPrice}
                  helperText={formErrors.sellingPrice}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={selectedTax?.rate ? `Retailer With Tax (${selectedTax.rate}%)` : 'Retailer With Tax'}
                  name="withTax"
                  type="number"
                  value={formData.withTax}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    endAdornment: selectedTax?.rate ? (
                      <InputAdornment position="end">
                        <Chip
                          label="With GST"
                          size="small"
                          color="success"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </InputAdornment>
                    ) : undefined
                  }}
                  helperText={selectedTax?.rate ? `Inclusive of ${selectedTax.rate}% tax` : ''}
                />
              </Grid>

              {/* Row 5b: Wholesale Rate and Wholesale With Tax */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Wholesale Rate"
                  name="wholesalePrice"
                  type="number"
                  value={formData.wholesalePrice}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  helperText="Optional"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={selectedTax?.rate ? `Wholesale With Tax (${selectedTax.rate}%)` : 'Wholesale With Tax'}
                  name="wholesaleWithTax"
                  type="number"
                  value={formData.wholesaleWithTax}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  helperText={selectedTax?.rate ? `Inclusive of ${selectedTax.rate}% tax` : ''}
                />
              </Grid>

              {/* Row 6: Reorder Level and Status */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Reorder Level"
                  name="reorderLevel"
                  type="number"
                  value={formData.reorderLevel}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <InventoryIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  value={formData.status}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, status: newValue || 'ACTIVE' }));
                  }}
                  options={['ACTIVE', 'INACTIVE']}
                  disabled={isViewMode || loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      size="small"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* UOM Management Section */}
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', mt: 2 }}>


            <Grid container spacing={3}>
              {/* Main Unit */}
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="primary">
                  Main Unit
                </Typography>
                <Autocomplete
                  value={selectedUnit}
                  onChange={(event, newValue) => {
                    if (newValue === 'add-new') {
                      setUnitDialogOpen(true);
                    } else {
                      setSelectedUnit(newValue);
                      setFormData(prev => ({
                        ...prev,
                        unitId: newValue ? String(newValue.id) : ''
                      }));
                    }
                  }}
                  options={[...units, 'add-new']}
                  getOptionLabel={(option) => {
                    if (option === 'add-new') return '+ Add New Unit';
                    return option?.abbreviation || option?.name || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (option === 'add-new') return false;
                    return option?.id === value?.id;
                  }}
                  loading={loadingUnits}
                  disabled={isViewMode || loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Main Unit"
                      required
                      size="small"
                      error={!!formErrors.unitId}
                      helperText={formErrors.unitId}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <UnitIcon color="primary" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <TextField
                  fullWidth
                  required
                  label="Price"
                  name="sellingPrice"
                  type="number"
                  size="small"
                  value={formData.sellingPrice}
                  onChange={handleChange}
                  disabled={isViewMode || loading}
                  error={!!formErrors.sellingPrice}
                  helperText={formErrors.sellingPrice}
                  sx={{ mt: 2 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>

              {/* Sub Unit 1 */}
              <Grid item xs={12} md={4.5}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Sub Unit 1
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="h6" sx={{ minWidth: 30, textAlign: 'center' }}>=</Typography>
                  <TextField
                    size="small"
                    name="subUnit1ConversionRatio"
                    type="number"
                    value={formData.subUnit1ConversionRatio}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty or valid number with max 2 decimal places
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        handleChange(e);
                      }
                    }}
                    onWheel={(e) => {
                      e.target.blur(); // Prevent scrolling from changing value
                    }}
                    disabled={isViewMode || loading}
                    placeholder="Qty"
                    sx={{
                      width: 100,
                      '& input[type=number]': {
                        MozAppearance: 'textfield',
                      },
                      '& input[type=number]::-webkit-outer-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      '& input[type=number]::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  <Autocomplete
                    value={selectedSubUnit1}
                    onChange={(event, newValue) => {
                      if (newValue === 'add-new') {
                        setUnitDialogOpen(true);
                      } else {
                        setSelectedSubUnit1(newValue);
                        setFormData(prev => ({
                          ...prev,
                          subUnit1Id: newValue ? String(newValue.id) : '',
                          subUnit1ConversionRatio: newValue ? prev.subUnit1ConversionRatio : '',
                          subUnit1SellingPrice: newValue ? prev.subUnit1SellingPrice : ''
                        }));
                      }
                    }}
                    options={[...units, 'add-new']}
                    getOptionLabel={(option) => {
                      if (option === 'add-new') return '+ Add New Unit';
                      return option?.abbreviation || option?.name || '';
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (option === 'add-new') return false;
                      return option?.id === value?.id;
                    }}
                    loading={loadingUnits}
                    disabled={isViewMode || loading}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sub Unit 1"
                        size="small"
                      />
                    )}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ minWidth: 30, textAlign: 'center', visibility: 'hidden' }}>=</Typography>
                  <TextField
                    size="small"
                    label="Price"
                    name="subUnit1SellingPrice"
                    type="number"
                    value={formData.subUnit1SellingPrice}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        handleChange(e);
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    disabled={isViewMode || loading}
                    placeholder="Auto or manual"
                    sx={{
                      width: 100,
                      flex: 1,
                      '& input[type=number]': { MozAppearance: 'textfield' },
                      '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                      '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Box>
              </Grid>

              {/* Sub Unit 2 */}
              <Grid item xs={12} md={4.5}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Sub Unit 2
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="h6" sx={{ minWidth: 30, textAlign: 'center' }}>=</Typography>
                  <TextField
                    size="small"
                    name="subUnit2ConversionRatio"
                    type="number"
                    value={formData.subUnit2ConversionRatio}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty or valid number with max 2 decimal places
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        handleChange(e);
                      }
                    }}
                    onWheel={(e) => {
                      e.target.blur(); // Prevent scrolling from changing value
                    }}
                    disabled={isViewMode || loading || !formData.subUnit1Id}
                    placeholder="Qty"
                    sx={{
                      width: 100,
                      '& input[type=number]': {
                        MozAppearance: 'textfield',
                      },
                      '& input[type=number]::-webkit-outer-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      '& input[type=number]::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  <Autocomplete
                    value={selectedSubUnit2}
                    onChange={(event, newValue) => {
                      if (newValue === 'add-new') {
                        setUnitDialogOpen(true);
                      } else {
                        setSelectedSubUnit2(newValue);
                        setFormData(prev => ({
                          ...prev,
                          subUnit2Id: newValue ? String(newValue.id) : '',
                          subUnit2ConversionRatio: newValue ? prev.subUnit2ConversionRatio : '',
                          subUnit2SellingPrice: newValue ? prev.subUnit2SellingPrice : ''
                        }));
                      }
                    }}
                    options={[...units, 'add-new']}
                    getOptionLabel={(option) => {
                      if (option === 'add-new') return '+ Add New Unit';
                      return option?.abbreviation || option?.name || '';
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (option === 'add-new') return false;
                      return option?.id === value?.id;
                    }}
                    loading={loadingUnits}
                    disabled={isViewMode || loading || !formData.subUnit1Id}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sub Unit 2"
                        size="small"
                      />
                    )}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ minWidth: 30, textAlign: 'center', visibility: 'hidden' }}>=</Typography>
                  <TextField
                    size="small"
                    label="Price"
                    name="subUnit2SellingPrice"
                    type="number"
                    value={formData.subUnit2SellingPrice}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        handleChange(e);
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    disabled={isViewMode || loading || !formData.subUnit1Id}
                    placeholder="Auto or manual"
                    sx={{
                      width: 100,
                      flex: 1,
                      '& input[type=number]': { MozAppearance: 'textfield' },
                      '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                      '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right Column - Image */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Product Image
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {formData.imagePreview ? (
                <Box
                  component="img"
                  src={formData.imagePreview}
                  alt="Product"
                  sx={{
                    width: '100%',
                    maxHeight: 300,
                    objectFit: 'contain',
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'grey.50'
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: 300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'grey.100',
                    border: '2px dashed',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  <ImageIcon sx={{ fontSize: 80, color: 'grey.400' }} />
                </Box>
              )}

              {!isViewMode && (
                <Stack spacing={1} sx={{ width: '100%' }}>
                  <Button
                    variant="contained"
                    component="label"
                    disabled={loading || uploadLoading}
                    startIcon={uploadLoading ? <CircularProgress size={20} /> : <UploadIcon />}
                    fullWidth
                  >
                    {uploadLoading ? 'Uploading...' : 'Upload Image'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Button>
                  {formData.imagePreview && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveImage}
                      disabled={loading || uploadLoading}
                      startIcon={<DeleteIcon />}
                      fullWidth
                    >
                      Remove Image
                    </Button>
                  )}
                </Stack>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                Max size: 1KB - 500KB • Formats: JPEG, PNG
              </Typography>
              {imageError && (
                <Typography variant="caption" color="error" sx={{ mt: 1, textAlign: 'center' }}>
                  {imageError}
                </Typography>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.manageable}
                    onChange={(e) => setFormData(prev => ({ ...prev, manageable: e.target.checked }))}
                    disabled={isViewMode || loading}
                    color="primary"
                  />
                }
                label="Stock required"
                sx={{ mt: 2 }}
              />

              <TextField
                fullWidth
                size="small"
                label="Remark"
                name="productCode"
                value={formData.productCode}
                onChange={handleChange}
                disabled={isViewMode || loading}
                sx={{ mt: 2 }}
                placeholder="Product Code / Remark"
              />

              <Autocomplete
                value={selectedSupplier}
                onChange={(event, newValue) => {
                  setSelectedSupplier(newValue);
                  setFormData(prev => ({
                    ...prev,
                    supplierId: newValue ? String(newValue.id) : ''
                  }));
                }}
                options={suppliers}
                getOptionLabel={(option) => (option?.supplierCode ? `${option.name || ''} (${option.supplierCode})` : (option?.name || ''))}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                loading={loadingSuppliers}
                disabled={isViewMode || loading}
                autoHighlight
                sx={{ mt: 2 }}
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.supplierCode ? `${option.name} - ${option.supplierCode}` : option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Supplier (Optional)"
                    size="small"
                    helperText="Used for barcode label supplier code"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SupplierIcon color="primary" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      {!isViewMode && (
        <Paper elevation={0} sx={{ p: 2, mt: 3, bgcolor: 'grey.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" fontWeight={600} color="primary">
              Keyboard Shortcuts:
            </Typography>
            <Chip label="Tab: Next Field" size="small" variant="outlined" />
            <Chip label="Shift+Tab: Previous" size="small" variant="outlined" />
            <Chip label="Ctrl+S: Save" size="small" variant="outlined" />
            <Chip label="Esc: Cancel" size="small" variant="outlined" />


          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              ref={submitButtonRef}
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              size="large"
            >
              {loading ? 'Saving...' : (initialData ? 'Update Product' : 'Create Product')}
            </Button>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
              startIcon={<CloseIcon />}
            >
              Cancel
            </Button>
          </Stack>
        </Paper>
      )}




      {/* Quick Create Dialogs */}
      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Category</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Category Name"
              value={newCategory.name}
              onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateCategory} variant="contained" startIcon={<AddIcon />}>
            Create
          </Button>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Unit Dialog */}
      <Dialog open={unitDialogOpen} onClose={() => setUnitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Unit</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Unit Name"
              value={newUnit.name}
              onChange={(e) => setNewUnit(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Kilogram, Piece, Box"
            />
            <TextField
              fullWidth
              label="Abbreviation"
              value={newUnit.abbreviation}
              onChange={(e) => setNewUnit(prev => ({ ...prev, abbreviation: e.target.value }))}
              placeholder="e.g., kg, pcs, box"
              onKeyPress={(e) => e.key === 'Enter' && handleCreateUnit()}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateUnit} variant="contained" startIcon={<AddIcon />}>
            Create
          </Button>
          <Button onClick={() => setUnitDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Tax Dialog */}
      <Dialog open={taxDialogOpen} onClose={() => setTaxDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Tax</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Tax Name"
              value={newTax.name}
              onChange={(e) => setNewTax(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., GST, VAT"
            />
            <TextField
              fullWidth
              label="Tax Rate (%)"
              type="number"
              value={newTax.rate}
              onChange={(e) => setNewTax(prev => ({ ...prev, rate: e.target.value }))}
              placeholder="e.g., 18"
              onKeyPress={(e) => e.key === 'Enter' && handleCreateTax()}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateTax} variant="contained" startIcon={<AddIcon />}>
            Create
          </Button>
          <Button onClick={() => setTaxDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Keyboard Shortcuts & Tips</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="h6">Navigation</Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li><strong>Tab:</strong> Move to next field</li>
              <li><strong>Shift+Tab:</strong> Move to previous field</li>
              <li><strong>Ctrl+S:</strong> Save product</li>
              <li><strong>Esc:</strong> Cancel and close</li>
              <li><strong>Enter:</strong> Submit when in dialogs</li>
            </Box>

            <Typography variant="h6">Quick Actions</Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>Type in Category/Unit/Tax fields and select "+ Add New" to create inline</li>
              <li>SKU will be auto-generated if left empty</li>
              <li>Use arrow keys to navigate autocomplete suggestions</li>
            </Box>

            <Typography variant="h6">Tips</Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>All fields with * are required</li>
              <li>Min sale price cannot exceed selling price</li>
              <li>Image uploads are processed instantly</li>
              <li>Changes are not saved until you click Save or press Ctrl+S</li>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)} variant="contained">
            Got it!
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductFormEnhanced;
